import { type TypeSelectOption, toastService } from '@thefarmersfront/kitchen';

import { registerMockAPI } from '~/mocks/mockApis';

import { useAPI, useAsyncHandler } from '~/hooks/common';
import { renderFilterErrorMessage, transformToSelectOptions } from '~/utils/common';

import { API_ENDPOINT } from '~/constants';

import type { TypeErrorResponseData } from '~/types/common.types';
import type { TypeGetCodesParams, TypeGetCodesResponse } from '~/types/order.types';

type TypeFormattedCodes = Record<keyof TypeGetCodesResponse['data']['codes'], TypeSelectOption<string>[]>;

/**
 * [출고주문관리 / 반품교환관리] 공통 코드 조회 api 요청 훅
 */
const useGetCodes = () => {
  const { runAsync } = useAsyncHandler();

  const getCodes = useAPI<TypeGetCodesParams, TypeGetCodesResponse>({
    endpoint: API_ENDPOINT.ORDER.GET_CODES,
    method: 'get',
  });

  const formatCodes = ({ codes }: TypeGetCodesResponse['data']): TypeFormattedCodes => {
    return Object.fromEntries(
      Object.entries(codes).map(([key, value]) => [key, transformToSelectOptions(value, 'code', 'description')])
    ) as TypeFormattedCodes;
  };

  const requestCodes = async (
    params: TypeGetCodesParams,
    {
      onSuccess,
      onError,
    }: {
      onSuccess?: (data: TypeFormattedCodes) => void;
      onError?: (error: TypeErrorResponseData['error']) => void;
    }
  ) => {
    runAsync(() => getCodes({ params }), {
      onSuccess: ({ data }) => {
        onSuccess?.(formatCodes(data));
      },
      onError: (error: TypeErrorResponseData['error']) => {
        toastService.addToast({
          type: 'error',
          message: renderFilterErrorMessage(),
        });
        onError?.(error);
      },
    });
  };

  return { requestCodes };
};

export default useGetCodes;

const toCodes = (entries: [code: string, description: string][]) =>
  entries.map(([code, description]) => ({ code, description }));

/** 출고주문·반품·교환 세 화면이 읽는 코드를 모두 담는다. 화면이 `codes.xxx[0]` 을 바로 읽어 키가 빠지면 화면이 깨진다. */
const codesNormalResponse: TypeGetCodesResponse = {
  data: {
    codes: {
      returnSearchKeyword: toCodes([
        ['RETURN_RECEIPT_ID', '반품요청번호'],
        ['CLIENT_ORDER_CODE', '원판매처 주문번호'],
        ['OUTBOUND_ORDER_CODE', '원출고요청번호'],
        ['RECEIVER_NAME', '수령자명'],
      ]),
      exchangeSearchKeyword: toCodes([
        ['EXCHANGE_RECEIPT_ID', '교환요청번호'],
        ['CLIENT_ORDER_CODE', '원판매처 주문번호'],
        ['OUTBOUND_ORDER_CODE', '원출고요청번호'],
        ['RECEIVER_NAME', '수령자명'],
      ]),
      returnStatus: toCodes([
        ['RETURN_RECEIPT_COMPLETE', '회수예정'],
        ['RETURN_DELIVERY_ING', '회수중'],
        ['RETURN_DELIVERY_CANCELED', '회수취소'],
        ['RETURN_DELIVERY_FAILED', '회수실패'],
        ['RETURN_INSPECTION_ING', '검수중'],
        ['RETURN_INSPECTION_SUCCESS', '검수성공'],
        ['RETURN_INSPECTION_FAILED', '검수실패'],
        ['RETURN_INSPECTION_DELAYED', '검수지연'],
      ]),
      orderSearchKeyword: toCodes([
        ['OUTBOUND_ORDER_CODE', '출고요청번호'],
        ['CLIENT_ORDER_CODE', '판매처 주문번호'],
        ['ORDERER_ID', '주문자 ID'],
        ['RECEIVER_NAME', '수령자명'],
      ]),
      // 반품·교환 신청 1단계는 첫 옵션이 배송예정일이어야 다른 입력 없이 검색된다.
      orderSearchDateType: toCodes([
        ['DELIVERY_DATE', '배송예정일'],
        ['PAYMENT_AT', '결제완료일시'],
      ]),
      outboundStatus: toCodes([
        ['READY', '대기중'],
        ['PRODUCING', '진행중'],
        ['COMPLETED', '완료'],
        ['CANCELED', '취소'],
      ]),
      deliveryPolicy: toCodes([
        ['DAWN', '새벽배송'],
        ['DAY', '당일배송'],
        ['PARCEL', '택배배송'],
      ]),
      courierCode: toCodes([
        ['KURLY', '컬리'],
        ['LOTTE', '롯데'],
        ['CJ', 'CJ대한통운'],
      ]),
      clusterCenter: toCodes([
        ['CC02', '김포'],
        ['CC03', '평택'],
        ['CC04', '창원'],
      ]),
      pickUpType: toCodes([
        ['DOOR_FRONT', '문 앞'],
        ['SECURITY_OFFICE', '경비실'],
        ['DELIVERY_BOX', '택배함'],
        ['ETC', '기타'],
      ]),
      accessMethod: toCodes([
        ['FREE', '자유출입 가능'],
        ['PASSWORD', '공동현관 비밀번호'],
        ['SECURITY_CALL', '경비실 호출'],
        ['ETC', '기타'],
      ]),
      returnReason: toCodes([
        ['CHANGE_OF_MIND', '단순변심'],
        ['DEFECT', '상품불량'],
        ['WRONG_DELIVERY', '오배송'],
        ['DAMAGED', '파손'],
      ]),
    },
  },
};

registerMockAPI<TypeGetCodesResponse>({
  method: 'get',
  endpoint: '/api-oms/codes',
  label: ['출고주문/출고주문조회/공통코드', '반품교환/반품신청조회/공통코드', '반품교환/교환신청조회/공통코드'],
  scenarios: [
    { name: '정상 응답', response: codesNormalResponse },
    {
      name: '서버 에러 (500)',
      response: { error: { code: 500, message: '공통 코드를 불러오지 못했습니다.' } },
      status: 500,
    },
  ],
});
